import { faAngleDown } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Button, Menu, MenuItem, Stack, Typography } from '@mui/material'
import React from 'react'
import COLORS from '../../../styles/colors'

export type CountryFlagSelectItem = {
  id: string
  name: string
  isocode: string
}

export type CountryFlagSelectProps = {
  countries: CountryFlagSelectItem[]
  selected?: CountryFlagSelectItem
  onSelect: (item: CountryFlagSelectItem) => void
}

// Only Denmark has its own flag, every other country falls back to an international globe.
const getFlagIconUrl = (isocode: string) =>
  isocode.toUpperCase() === 'DK'
    ? 'https://hatscripts.github.io/circle-flags/flags/dk.svg'
    : undefined

// Shown as the content language rather than the raw country name (e.g. Danish, not Denmark).
const getLanguageLabel = (isocode: string) =>
  isocode.toUpperCase() === 'DK' ? 'Danish' : 'English'

export const CountryFlagCircle = ({ isocode }: { isocode: string }) => {
  const flagIconUrl = getFlagIconUrl(isocode)

  return (
    <Box
      sx={{
        alignItems: 'center',
        borderRadius: '50%',
        display: 'flex',
        flexShrink: 0,
        fontSize: '24px',
        height: '24px',
        justifyContent: 'center',
        lineHeight: 1,
        overflow: 'hidden',
        width: '24px',
      }}
    >
      {flagIconUrl ? (
        // eslint-disable-next-line @next/next/no-img-element -- small external SVG icon, not worth Next/Image optimization
        <img alt="" height={24} src={flagIconUrl} width={24} />
      ) : (
        '🌐'
      )}
    </Box>
  )
}

const CountryFlagSelect = ({
  countries,
  selected,
  onSelect,
}: CountryFlagSelectProps) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const handleClose = () => setAnchorEl(null)

  if (countries.length === 0) {
    return null
  }

  return (
    <>
      <Button
        disableElevation
        disableRipple
        onClick={(event) => setAnchorEl(event.currentTarget)}
        endIcon={
          <FontAwesomeIcon
            color={COLORS.grey}
            icon={faAngleDown}
            style={{ height: '12px', width: '12px' }}
          />
        }
        sx={{
          backgroundColor: 'transparent',
          borderRadius: '4px',
          color: COLORS.greyDark,
          minWidth: 'unset',
          px: '12px',
          py: '8px',
          textTransform: 'none',
          transition: 'background-color 0.2s ease-in-out',
          width: 'fit-content',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.05)',
          },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          {selected && <CountryFlagCircle isocode={selected.isocode} />}
          <Typography
            color="inherit"
            fontSize="14px"
            fontWeight={600}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {selected ? getLanguageLabel(selected.isocode) : ''}
          </Typography>
        </Stack>
      </Button>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {countries.map((country) => (
          <MenuItem
            key={country.id}
            onClick={() => {
              onSelect(country)
              handleClose()
            }}
            selected={selected?.id === country.id}
            sx={{
              borderRadius: '4px',
              backgroundColor:
                selected?.id === country.id
                  ? 'rgba(209, 64, 6, 0.08)'
                  : 'transparent',
              '&:hover': {
                backgroundColor:
                  selected?.id === country.id
                    ? 'rgba(209, 64, 6, 0.08)'
                    : 'rgba(0, 0, 0, 0.05)',
              },
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <CountryFlagCircle isocode={country.isocode} />
              <Typography
                color={COLORS.greyDark}
                fontSize="14px"
                fontWeight={selected?.id === country.id ? 700 : 400}
              >
                {getLanguageLabel(country.isocode)}
              </Typography>
            </Stack>
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

export default CountryFlagSelect
